import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, MapPin, Calendar, Users, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';

const CountdownTimer: React.FC<{ proposedDate: string; startTime?: string }> = ({ proposedDate, startTime }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const targetDate = new Date(proposedDate);
  if (startTime) {
    const [h, m] = startTime.split(':').map(Number);
    targetDate.setHours(h, m, 0, 0);
  }

  const diff = targetDate.getTime() - now;
  if (diff <= 0) return null;

  const totalMinutes = Math.floor(diff / 60_000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  let label: string;
  if (totalMinutes < 60) {
    label = 'Starting soon';
  } else if (days > 0) {
    label = `${days}d ${hours}h`;
  } else {
    label = `${hours}h ${minutes}m`;
  }

  const isUrgent = totalMinutes < 180;

  return (
    <div className={`flex items-center gap-1.5 text-xs font-medium ${isUrgent ? 'text-amber-600 dark:text-amber-400' : 'text-primary'}`}>
      <Clock className="h-3.5 w-3.5" />
      <span>{label}</span>
    </div>
  );
};

interface TournamentData {
  id: string;
  name: string;
  group_id: string;
  proposed_date: string;
  start_time?: string;
  club_id: string | null;
  status: string;
  groups: {
    name: string;
  } | null;
  padel_clubs?: {
    id?: string;
    club_name: string;
    street_address?: string;
  } | null;
}

interface UpcomingTournamentsWidgetProps {
  tournaments: {
    participating: TournamentData[];
    available: TournamentData[];
  };
  loading: boolean;
}

export const UpcomingTournamentsWidget: React.FC<UpcomingTournamentsWidgetProps> = ({
  tournaments,
  loading,
}) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-purple-600" />
            Upcoming Tournaments
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  const allTournaments = [
    ...tournaments.participating.map(t => ({ ...t, isParticipating: true })),
    ...tournaments.available.map(t => ({ ...t, isParticipating: false })),
  ];

  const displayTournaments = allTournaments.slice(0, 3);
  const hasMore = allTournaments.length > 3;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
    >
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-purple-600" />
              Upcoming Tournaments
            </CardTitle>
            {hasMore && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/tournaments')}
                className="text-sm"
              >
                View All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {displayTournaments.map((tournament, index) => (
            <motion.div
              key={tournament.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index }}
              onClick={() => navigate(`/groups/${tournament.group_id}/tournaments/${tournament.id}`)}
              className="p-4 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors"
            >
                  <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-sm truncate">{tournament.name}</h4>
                    <Badge
                      variant="secondary"
                      className={
                        tournament.isParticipating && tournament.status === 'in_progress'
                          ? "bg-orange-100 text-orange-700 hover:bg-orange-100"
                          : tournament.isParticipating && tournament.status === 'registration_closed'
                          ? "bg-purple-100 text-purple-700 hover:bg-purple-100"
                          : tournament.isParticipating
                          ? "bg-green-100 text-green-700 hover:bg-green-100"
                          : "bg-blue-100 text-blue-700 hover:bg-blue-100"
                      }
                    >
                      {tournament.isParticipating && tournament.status === 'in_progress'
                        ? "In Progress"
                        : tournament.isParticipating && tournament.status === 'registration_closed'
                        ? "Confirmed ✓"
                        : tournament.isParticipating
                        ? "Registered"
                        : "Register Now"}
                    </Badge>
                  </div>
                  
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5" />
                      <span className="truncate">{tournament.groups?.name || 'Unknown Group'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>
                        {format(new Date(tournament.proposed_date), 'MMM dd, yyyy')}
                        {tournament.start_time && ` • ${tournament.start_time.substring(0, 5)}`}
                      </span>
                    </div>
                    {tournament.padel_clubs?.club_name && (
                      <div className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" />
                        <span className="truncate">
                          {tournament.padel_clubs.club_name}
                          {tournament.padel_clubs.street_address && `, ${tournament.padel_clubs.street_address}`}
                        </span>
                      </div>
                    )}
                    {(tournament.status === 'registration_closed' || tournament.status === 'in_progress') && (
                      <CountdownTimer proposedDate={tournament.proposed_date} startTime={tournament.start_time} />
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </CardContent>
      </Card>
    </motion.div>
  );
};
